/**
 * Regex tables and matchers that detect AI-authored timestamp and duration
 * mentions in prose. Pure string-in, structured-out — no React, no markdown
 * AST. The remark plugin (see ./remarkTimestampMentions.ts) consumes these
 * matchers.
 *
 * Instants are recognized in four units, plus a unitless form:
 *
 *   t=<digits>ns                → nanoseconds
 *   t=<digits>(µs|μs|us)        → microseconds
 *   t=<digits>ms                → milliseconds
 *   t=<digits>[.<frac>]s        → seconds (lossless via string math)
 *   t=<digits>                  → nanoseconds
 *
 * Durations are recognized only as `d=<signed digits>ns` (see
 * {@link findDurationMentions}).
 *
 * The `t=` envelope is the discriminator for prose mentions, but not
 * the only one: the value inside it must also be a plausible epoch instant
 * (see {@link isPlausibleEpochNs}). Bare digits and any other prefix pass
 * through unchanged — except directly after an enveloped mention, where a
 * `-`, `–`, `—` or ` to ` separator continues it into an interval whose
 * values are all at or after the enveloped one (see
 * {@link matchContinuation}). A descending range (`t=<B>ns-<A>ns`) is
 * deliberately not rescued: its far end reads as a duration.
 */

export interface TimestampMention {
  start: number;
  end: number;
  raw: string;
  ns: bigint;
}

// An envelope alone does not make a value an instant: models write durations
// inside one too. A real reply carried `t=10000000000ns` meaning "10 s", and
// the chat rendered those ten billion nanoseconds against the reader's anchor
// as `-1736852206.178 s`. Anything outside the plausible epoch range is left
// as the model wrote it — no formatting, no highlight, no "Set as t=0" — and
// is deliberately NOT re-rendered as a duration either: the detector's job is
// to recognize instants, not to reinterpret what it rejects.
//
// The floor sits well before any robotics data yet far above any duration a
// model would plausibly write: ten days is 8.64e14 ns against the floor's
// 4.73e17, more than 500x of headroom, so the two populations separate
// without a unit-aware heuristic. The ceiling catches a digit
// appended by a typo — 1.77e19 ns is the year 2531.
const MIN_PLAUSIBLE_EPOCH_NS = 473_385_600_000_000_000n; // 1985-01-01T00:00:00Z
const MAX_PLAUSIBLE_EPOCH_NS = 4_102_444_800_000_000_000n; // 2100-01-01T00:00:00Z

/**
 * True when `ns` could be a wall-clock instant rather than a duration or a
 * typo: within [1985-01-01, 2100-01-01] UTC, inclusive at both ends.
 *
 * Every path that turns matched digits into a mention is gated on this, so a
 * clock-broken recording anchored near 1970 is no longer rescued by the
 * envelope — that trade was made deliberately (see the note in
 * {@link matchContinuation}).
 */
export function isPlausibleEpochNs(ns: bigint): boolean {
  return ns >= MIN_PLAUSIBLE_EPOCH_NS && ns <= MAX_PLAUSIBLE_EPOCH_NS;
}

/**
 * True when a value labeled as a duration (a `*_ns` column, a `d=<n>ns`
 * mention) is an epoch instant instead: its magnitude lies in the
 * {@link isPlausibleEpochNs} window. Every such "duration" is at least 15
 * years, so none is real, and rendering it as one states a false fact
 * (`first_ns` holding `MIN(_roboto_timestamp)` read as "20503d 6h").
 */
export function isEpochInstantMagnitude(ns: bigint): boolean {
  return isPlausibleEpochNs(ns < 0n ? -ns : ns);
}

// Closes a unitless value (the last ENVELOPED_REGEXES entry, and the bare far
// end of a range in BARE_CONTINUATION_REGEX): no further digit, no fraction, no
// unit. A period that ends a sentence is not a fraction.
const UNITLESS_END = String.raw`(?!\d|\.\d|\s*(?:ns|µs|μs|us|ms|s)\b)`;

// Digit widths are unconstrained: the plausibility range, not a digit count,
// decides what is an instant, so a value written in any of the four units
// round-trips as long as it lands in the window. Bare decimal numbers in
// prose ("took 3.4s") remain rejected because they lack the `t=` prefix.
const ENVELOPED_REGEXES: {
  re: RegExp;
  toNs: (intPart: string, fracPart?: string) => bigint;
}[] = [
  {
    // ns
    re: /(?<![A-Za-z0-9_.])[~≈]?\s*t\s*=\s*(\d+)\s*ns\b/g,
    toNs: (intPart) => BigInt(intPart),
  },
  {
    // µs — any of the three micro spellings
    re: /(?<![A-Za-z0-9_.])[~≈]?\s*t\s*=\s*(\d+)\s*(?:µs|μs|us)\b/g,
    toNs: (intPart) => BigInt(intPart) * 1_000n,
  },
  {
    // ms
    re: /(?<![A-Za-z0-9_.])[~≈]?\s*t\s*=\s*(\d+)\s*ms\b/g,
    toNs: (intPart) => BigInt(intPart) * 1_000_000n,
  },
  {
    // s — optional fractional (up to 9 digits) kept lossless
    re: /(?<![A-Za-z0-9_.])[~≈]?\s*t\s*=\s*(\d+)(?:\.(\d{1,9}))?\s*s\b/g,
    toNs: (intPart, fracPart) => {
      const frac = (fracPart ?? "").padEnd(9, "0").slice(0, 9);
      return BigInt(intPart) * 1_000_000_000n + BigInt(frac);
    },
  },
  {
    // No unit: read as ns. The AI tools print instants as bare ns integers, and
    // models copy them into `t=` without the suffix. An epoch value written in
    // a coarser unit cannot be misread this way: 10-digit s, 13-digit ms, and
    // 16-digit µs values all land in 1970 when read as ns, outside the
    // plausibility window. The lookahead leaves suffixed and fractional values
    // to the entries above, while a period that ends a sentence still closes
    // the value; its `\d` stops `(\d+)` from backtracking to a shorter digit
    // run that would dodge the unit check. With no unit to mark it, a `t=`
    // that is a URL query or LaTeX parameter (`?t=`, `&t=`, `$t=`, anything
    // after `://` in the same token) is not a mention.
    re: new RegExp(
      String.raw`(?<![A-Za-z0-9_.?&=/$])(?<!://\S*)[~≈]?\s*t\s*=\s*(\d+)` +
        UNITLESS_END,
      "g",
    ),
    toNs: (intPart) => BigInt(intPart),
  },
];

export function findTimestampMentions(text: string): TimestampMention[] {
  // Every enveloped pattern needs a literal `=`, and continuations are only
  // ever seeded from an enveloped match, so prose without one cannot hold a
  // mention. Rejecting it here skips the regex scans on ordinary text nodes.
  if (!text.includes("=")) {
    return [];
  }
  const mentions: TimestampMention[] = [];
  for (const { re, toNs } of ENVELOPED_REGEXES) {
    re.lastIndex = 0;
    for (const m of text.matchAll(re)) {
      const ns = toNs(m[1], m[2]);
      // The regex may match; the classifier still rejects. An implausible
      // value is not a mention at all, so it also never anchors a
      // continuation below — the whole run stays literal prose.
      if (!isPlausibleEpochNs(ns)) {
        continue;
      }
      const start = m.index ?? 0;
      mentions.push({
        start,
        end: start + m[0].length,
        raw: m[0],
        ns,
      });
    }
  }
  // Overlap resolution depends on TWO coupled invariants — preserve both
  // when editing this code:
  //   (a) `ENVELOPED_REGEXES` is iterated in declaration order, so the
  //       higher-precision unit (ns first) is pushed before less-specific
  //       overlapping units. Reordering the array would flip precedence.
  //   (b) `Array.prototype.sort` is stable in V8 / modern engines, so on
  //       equal `(start, -end)` keys the first-pushed match wins. Swapping
  //       in `Array.prototype.toSorted` or any unstable sort would silently
  //       break tie-breaking and let lower-precision matches win.
  mentions.sort((a, b) => a.start - b.start || b.end - a.end);
  const out: TimestampMention[] = [];
  let lastEnd = -1;
  for (const m of mentions) {
    if (m.start < lastEnd) {
      continue;
    }
    out.push(m);
    lastEnd = m.end;
    // Each accepted mention may anchor an interval the model wrote under a
    // single envelope; every continuation extends the run. The enveloped
    // value is the floor for the whole run, so a chain need not be
    // monotonic (`t=100ms-300ms-200ms` converts all three). A unitless anchor
    // (its raw text ends in a digit) also takes unitless far ends.
    const unitless = /\d$/.test(m.raw);
    for (
      let cont = matchContinuation(text, lastEnd, m.ns, unitless);
      cont !== null;
      cont = matchContinuation(text, lastEnd, m.ns, unitless)
    ) {
      out.push(cont);
      lastEnd = cont.end;
    }
  }
  return out;
}

// `d=<signed integer>ns`: the envelope the AI tools print durations in and the
// prompt asks the model to copy. Nanoseconds only. The epoch window gates it in
// reverse: a value inside it is an instant written as a duration, left as the
// model wrote it (see {@link isEpochInstantMagnitude}).
const DURATION_REGEX = /(?<![A-Za-z0-9_.])d\s*=\s*([-+]?\d+)\s*ns\b/g;

/**
 * Find AI-authored `d=<n>ns` duration mentions in prose. A match holds only
 * `d=`, a signed integer, `ns`, and whitespace, so it never overlaps a mention
 * returned by {@link findTimestampMentions}.
 */
export function findDurationMentions(text: string): TimestampMention[] {
  if (!text.includes("=")) {
    return [];
  }
  return Array.from(text.matchAll(DURATION_REGEX), (m) => {
    const start = m.index ?? 0;
    return {
      start,
      end: start + m[0].length,
      raw: m[0],
      ns: BigInt(m[1]),
    };
  }).filter((mention) => !isEpochInstantMagnitude(mention.ns));
}

/**
 * True when the entire trimmed string is a single `t=<n><unit>` mention.
 */
export function isTimestampMention(text: string): boolean {
  const trimmed = text.trim();
  if (trimmed.length === 0) {
    return false;
  }
  const mentions = findTimestampMentions(trimmed);
  return (
    mentions.length === 1 &&
    mentions[0].start === 0 &&
    mentions[0].end === trimmed.length
  );
}

// ---------------------------------------------------------------------------
// Bare-unit mentions (no `t=` prefix)
// ---------------------------------------------------------------------------
// Used to rescue timestamps the AI wrote with the `t=` prefix outside a
// backticked highlight (e.g. ``t=`1771481251863355727ns```). The digits+unit
// half ends up as an inlineCode node; the `t=` preamble sits in the
// preceding text sibling.

const BARE_UNIT_REGEXES: {
  re: RegExp;
  toNs: (intPart: string, fracPart?: string) => bigint;
}[] = [
  { re: /^\s*(\d+)\s*ns\s*$/, toNs: (i) => BigInt(i) },
  { re: /^\s*(\d+)\s*(?:µs|μs|us)\s*$/, toNs: (i) => BigInt(i) * 1_000n },
  { re: /^\s*(\d+)\s*ms\s*$/, toNs: (i) => BigInt(i) * 1_000_000n },
  {
    re: /^\s*(\d+)(?:\.(\d{1,9}))?\s*s\s*$/,
    toNs: (i, f) => {
      const frac = (f ?? "").padEnd(9, "0").slice(0, 9);
      return BigInt(i) * 1_000_000_000n + BigInt(frac);
    },
  },
  // No unit: ns, as for the unitless `t=` entry (``t=`<digits>` ``, and the
  // bare far end of a range after a unitless anchor).
  { re: /^\s*(\d+)\s*$/, toNs: (i) => BigInt(i) },
];

/**
 * Match a unit-only mention (no `t=` prefix) that fills the entire string.
 *
 * Gated on {@link isPlausibleEpochNs} like the enveloped pass, which is what
 * keeps an implausible far end (`t=<plausible>ns-10000000000ns`) from being
 * rescued as the far end of a range and an implausible split highlight
 * (``t=`10000000000ns` ``) from being upgraded to a marker.
 */
export function matchBareUnitMention(
  text: string,
): { ns: bigint; raw: string } | null {
  for (const { re, toNs } of BARE_UNIT_REGEXES) {
    const m = text.match(re);
    if (m) {
      const ns = toNs(m[1], m[2]);
      return isPlausibleEpochNs(ns) ? { ns, raw: text } : null;
    }
  }
  return null;
}

// ---------------------------------------------------------------------------
// Continuations
// ---------------------------------------------------------------------------
// The prompt asks the model to write an interval as two independently
// enveloped timestamps (`t=<A>ns to t=<B>ns`), but it routinely emits one
// envelope spanning both (`t=<A>ns-<B>ns`). The far end is then bare digits,
// which the enveloped pass cannot see: it stays raw epoch nanoseconds beside
// a re-rendered near end, which is how a customer read
// `1786643755.837349000 s-1786643878337759000ns` in one line.
//
// Anchoring on a mention the enveloped pass already accepted keeps the
// bare-digit rule intact everywhere else — a duration in prose ("took 3.4s")
// is still not a timestamp — and lets a chain (`A-B-C`) fall out of the loop
// in findTimestampMentions rather than needing its own shape.

// Shape only, mirroring BARE_UNIT_REGEXES above (a fractional part is legal
// on `s` alone): matchBareUnitMention stays the authority on the value, so a
// unit added to that table has to be added here too. Sticky, so the
// continuation must start exactly where the previous value ended — no
// scanning ahead into unrelated prose, and the span is disjoint from its
// predecessor's by construction (rewriteTextNode splices on those spans).
const CONTINUATION_REGEX =
  /(?:\s*[-–—]\s*|\s+to\s+)(\d+(?:\.\d{1,9})?\s*s|\d+\s*(?:ns|µs|μs|us|ms))\b/y;

// The far end of a range whose anchor had no unit (`t=<A>-<B>`): bare digits,
// closed like the unitless `t=` entry.
const BARE_CONTINUATION_REGEX = new RegExp(
  String.raw`(?:\s*[-–—]\s*|\s+to\s+)(\d+)` + UNITLESS_END,
  "y",
);

/**
 * Match the value continuing an interval at `from` (the end of the previous
 * value), or null if the text there is not one. The first value that fails
 * ends the run; nothing after it is rescued. Bare digits continue the run
 * only when `unitless` (the anchor had no unit either).
 */
function matchContinuation(
  text: string,
  from: number,
  floorNs: bigint,
  unitless: boolean,
): TimestampMention | null {
  CONTINUATION_REGEX.lastIndex = from;
  BARE_CONTINUATION_REGEX.lastIndex = from;
  const m =
    CONTINUATION_REGEX.exec(text) ??
    (unitless ? BARE_CONTINUATION_REGEX.exec(text) : null);
  const value = m === null ? null : matchBareUnitMention(m[1]);
  // A value before the enveloped anchor is a duration rather than the far end
  // of an interval ("t=<A>ns - 3.4s of skew") and stays untouched; one equal
  // to it is an empty interval and is accepted.
  //
  // This is now the second duration guard, and it still earns its keep: the
  // plausibility range added above resolves the near-zero-anchor case this
  // note used to concede ("t=5ns - 3.4s of skew" no longer has an anchor at
  // all, so neither value is converted), but it cannot separate a descending
  // range from a duration when both ends land inside the window. The floor
  // still does that.
  if (m === null || value === null || value.ns < floorNs) {
    return null;
  }
  const end = m.index + m[0].length;
  return { start: end - m[1].length, end, raw: m[1], ns: value.ns };
}

/**
 * Match a trailing `t\s*=\s*` on a text node, returning the prefix to keep
 * and confirming the suffix existed. The prefix's trailing whitespace is
 * preserved so rewritten output keeps the natural spacing around the
 * mention (`"found "` stays `"found "`). Approximation marks (`~`, `≈`)
 * are consumed with the `t=` token.
 */
export function stripTrailingTEquals(text: string): string | null {
  const m = text.match(/^(.*?)[~≈]?t\s*=\s*$/s);
  return m ? m[1] : null;
}
